import { useState } from 'react';
import {
  App,
  Avatar,
  Button,
  Card,
  Dropdown,
  Input,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps, MenuProps } from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  GoogleOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  useApps,
  useDeactivateApp,
  useReactivateApp,
  useToggleAppApproval,
} from '@/hooks/useApps';
import { useEnterprises } from '@/hooks/useEnterprises';
import type { AppDto } from '@/types/app';
import { AppFormModal } from './AppFormModal';
import PlayStoreIframeModal from './PlayStoreIframeModal';

const { Title, Text } = Typography;

export function AppsPage() {
  const { message } = App.useApp();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<AppDto | null>(null);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const { data: enterprises } = useEnterprises({ pageSize: 100 });
  const firstEnterpriseId = enterprises?.items?.[0]?.id ?? null;

  const { data, isLoading, refetch, isFetching } = useApps({
    page,
    pageSize,
    search: search || undefined,
    includeInactive,
    sortBy: 'title',
  });

  const toggleApproval = useToggleAppApproval();
  const deactivate = useDeactivateApp();
  const reactivate = useReactivateApp();

  const handleToggleApproval = async (a: AppDto) => {
    try {
      await toggleApproval.mutateAsync({ id: a.id, isApprovedInPlay: !a.isApprovedInPlay });
      message.success(`App "${a.title}" ${!a.isApprovedInPlay ? 'aprobada' : 'desaprobada'}.`);
    } catch {
      // interceptor
    }
  };

  const handleDeactivate = async (a: AppDto) => {
    try {
      await deactivate.mutateAsync(a.id);
      message.success(`App "${a.title}" desactivada.`);
    } catch {
      // interceptor
    }
  };

  const handleReactivate = async (a: AppDto) => {
    try {
      await reactivate.mutateAsync(a.id);
      message.success(`App "${a.title}" reactivada.`);
    } catch {
      // interceptor
    }
  };

  const columns: TableProps<AppDto>['columns'] = [
    {
      title: 'App',
      key: 'app',
      sorter: true,
      render: (_, a) => (
        <Space>
          <Avatar
            shape="square"
            size={40}
            src={a.iconUrl || undefined}
            icon={<AppstoreOutlined />}
            style={{ backgroundColor: 'var(--color-bg-elevated)' }}
          />
          <Space direction="vertical" size={0}>
            <Text strong>{a.title}</Text>
            <Text code style={{ fontSize: 12 }}>{a.packageName}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Categoria',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Aprobada en Play',
      key: 'approved',
      width: 180,
      align: 'center',
      render: (_, a) => (
        <Switch
          checked={a.isApprovedInPlay}
          onChange={() => handleToggleApproval(a)}
          loading={toggleApproval.isPending}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      ),
    },
    {
      title: 'Perfiles',
      dataIndex: 'profilesUsing',
      key: 'profilesUsing',
      width: 100,
      align: 'center',
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Estado',
      key: 'state',
      width: 110,
      render: (_, a) => a.isActive ? <Tag color="success">Activa</Tag> : <Tag color="error">Inactiva</Tag>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 80,
      align: 'right',
      render: (_, a) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: 'Editar',
            icon: <EditOutlined />,
            onClick: () => {
              setEditingApp(a);
              setFormOpen(true);
            },
          },
          { type: 'divider' as const },
          ...(a.isActive
            ? [{
                key: 'deactivate',
                label: 'Desactivar',
                icon: <StopOutlined />,
                danger: true,
                onClick: () => handleDeactivate(a),
              }]
            : [{
                key: 'reactivate',
                label: 'Reactivar',
                icon: <ReloadOutlined />,
                onClick: () => handleReactivate(a),
              }]),
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <AppstoreOutlined style={{ marginRight: 12 }} />
          Aplicaciones
        </Title>

        <Space>
          <Button
            icon={<GoogleOutlined />}
            size="large"
            onClick={() => setPlayModalOpen(true)}
            disabled={!firstEnterpriseId}
            title={!firstEnterpriseId ? 'Primero crea una empresa' : 'Buscar / subir desde Managed Google Play'}
          >
            Buscar en Play Store / Subir APK
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => {
              setEditingApp(null);
              setFormOpen(true);
            }}
          >
            Nueva App (manual)
          </Button>
        </Space>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Space>
            <Input
              placeholder="Buscar por nombre o package..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => setPage(1)}
              style={{ width: 320 }}
              allowClear
            />
            <Button onClick={() => { setPage(1); refetch(); }}>Buscar</Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refrescar
            </Button>
          </Space>

          <Space>
            <Text>Incluir inactivas:</Text>
            <Switch checked={includeInactive} onChange={(v) => { setIncludeInactive(v); setPage(1); }} />
          </Space>
        </div>

        <Table<AppDto>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: data?.totalCount ?? 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `${total} en total`,
          }}
        />
      </Card>

      <AppFormModal
        open={formOpen}
        app={editingApp}
        onClose={() => {
          setFormOpen(false);
          setEditingApp(null);
        }}
      />

      <PlayStoreIframeModal
        enterpriseId={firstEnterpriseId}
        open={playModalOpen}
        onClose={(registered) => {
          setPlayModalOpen(false);
          if (registered) refetch();
        }}
      />
    </div>
  );
}
